import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../../legal/PrivacyPolicy/PrivacyPolicy.css";

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const categories = [
    { id: "all", name: "All Topics" },
    { id: "getting-started", name: "Getting Started" },
    { id: "booking", name: "Booking Games" },
    { id: "account", name: "Account Management" },
    { id: "payment", name: "Payments & Billing" },
    { id: "safety", name: "Safety & Community" },
    { id: "technical", name: "Technical Support" },
  ];

  const faqs = [
    {
      id: 1,
      category: "getting-started",
      question: "How do I create an account?",
      answer:
        'Download the GoalFlex app from the App Store or Google Play, tap "Sign Up," and follow the prompts to create your profile. You\'ll need to provide your name, email, and set up your player preferences.',
    },
    {
      id: 2,
      category: "getting-started",
      question: "How do I find games near me?",
      answer:
        "Open the app and allow location access. Games near you will appear on the main screen. You can also use the search and filter options to find games by distance, skill level, and time.",
    },
    {
      id: 3,
      category: "booking",
      question: "How do I join a game?",
      answer:
        "Find a game you want to join, tap on it to see details, then tap \"Join Game.\" If there's a fee, you'll be prompted to complete payment. You'll receive confirmation once you're registered.",
    },
    {
      id: 4,
      category: "booking",
      question: "Can I cancel my booking?",
      answer:
        'Yes, you can cancel up to 24 hours before the game for a full refund. Cancellations within 24 hours will not receive a refund. Go to "My Games" and select "Cancel Booking."',
    },
    {
      id: 5,
      category: "account",
      question: "How do I update my profile?",
      answer:
        'Go to your profile tab, tap "Edit Profile," and update your information. You can change your skill level, position preferences, bio, and profile photo.',
    },
    {
      id: 6,
      category: "payment",
      question: "What payment methods do you accept?",
      answer:
        "We accept all major credit cards (Visa, MasterCard, American Express), Apple Pay, Google Pay, and PayPal. All payments are processed securely.",
    },
    {
      id: 7,
      category: "safety",
      question: "How do you ensure player safety?",
      answer:
        "All users go through identity verification. We have a rating system, community guidelines, and 24/7 support. Report any issues immediately through the app.",
    },
    {
      id: 8,
      category: "technical",
      question: "The app is not working properly. What should I do?",
      answer:
        "First, try closing and reopening the app. If issues persist, check for app updates in your app store. For continued problems, contact our support team with your device details.",
    },
  ];

  const filteredFAQs = faqs.filter((faq) => {
    const matchesCategory =
      selectedCategory === "all" || faq.category === selectedCategory;
    const matchesSearch =
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (id) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  return (
    <div className="legal-page">
      {/* Hero Section */}
      <section className="legal-hero">
        <div className="container">
          <h1>Help Center</h1>
          <p className="legal-subtitle">
            Find answers to your questions and get the help you need.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="legal-content">
        <div className="container">
          <div className="legal-intro">
            <p>
              Welcome to the GoalFlex Help Center! Here you'll find answers to
              common questions, step-by-step guides, and ways to get in touch
              with our support team.
            </p>
          </div>

          {/* Search and Filter */}
          <div className="legal-section">
            <h2>Search Help Articles</h2>
            <div style={{ marginBottom: "2rem" }}>
              <input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "1rem",
                  border: "2px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  marginBottom: "1rem",
                }}
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    style={{
                      padding: "0.5rem 1rem",
                      border: "none",
                      borderRadius: "20px",
                      background:
                        selectedCategory === category.id
                          ? "#10b981"
                          : "#e5e7eb",
                      color:
                        selectedCategory === category.id ? "white" : "#374151",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                    }}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="legal-section">
            <h2>Frequently Asked Questions</h2>
            <div style={{ maxWidth: "800px" }}>
              {filteredFAQs.map((faq) => (
                <div
                  key={faq.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    marginBottom: "1rem",
                    overflow: "hidden",
                  }}
                >
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    style={{
                      width: "100%",
                      padding: "1.5rem",
                      background: "white",
                      border: "none",
                      textAlign: "left",
                      fontSize: "1.1rem",
                      fontWeight: "600",
                      color: "#374151",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {faq.question}
                    <span style={{ fontSize: "1.5rem", color: "#10b981" }}>
                      {expandedFAQ === faq.id ? "−" : "+"}
                    </span>
                  </button>
                  {expandedFAQ === faq.id && (
                    <div
                      style={{
                        padding: "1.5rem",
                        backgroundColor: "#f9fafb",
                        borderTop: "1px solid #e5e7eb",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          color: "#4b5563",
                          lineHeight: "1.6",
                        }}
                      >
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="legal-section">
            <h2>Quick Start Guide</h2>
            <div className="subsection">
              <h3>Getting Started in 5 Steps</h3>
              <ul>
                <li>
                  <strong>Download:</strong> Get the GoalFlex app from your app
                  store
                </li>
                <li>
                  <strong>Sign Up:</strong> Create your account and complete
                  your profile
                </li>
                <li>
                  <strong>Find Games:</strong> Browse games near you or use
                  search filters
                </li>
                <li>
                  <strong>Join a Game:</strong> Book your spot and complete
                  payment if required
                </li>
                <li>
                  <strong>Play & Rate:</strong> Show up, play, and rate your
                  experience
                </li>
              </ul>
            </div>
          </div>

          <div className="legal-section">
            <h2>Account & Profile Help</h2>
            <div className="subsection">
              <h3>Profile Optimization</h3>
              <ul>
                <li>
                  Add a clear profile photo to build trust with other players
                </li>
                <li>Set accurate skill level and position preferences</li>
                <li>
                  Write a brief bio to help others know your playing style
                </li>
                <li>Keep your availability calendar updated</li>
              </ul>
            </div>
            <div className="subsection">
              <h3>Account Security</h3>
              <ul>
                <li>Use a strong, unique password</li>
                <li>Enable two-factor authentication if available</li>
                <li>Keep your email and phone number up to date</li>
                <li>Report any suspicious activity immediately</li>
              </ul>
            </div>
          </div>

          <div className="legal-section">
            <h2>Payment & Billing</h2>
            <div className="subsection">
              <h3>Understanding Fees</h3>
              <ul>
                <li>Games charge fees for venue rental and equipment</li>
                <li>Fees are clearly displayed before booking</li>
                <li>Payment is processed securely through the app</li>
              </ul>
            </div>
            <div className="subsection">
              <h3>Refund Policy</h3>
              <ul>
                <li>Free cancellation up to 24 hours before the game</li>
                <li>No refunds for cancellations within 24 hours</li>
                <li>Full refunds for weather-related cancellations</li>
                <li>No refunds for no-shows without valid reason</li>
              </ul>
            </div>
          </div>

          <div className="legal-section">
            <h2>Safety & Community Guidelines</h2>
            <ul>
              <li>
                <strong>Respect:</strong> Treat all players with respect and
                sportsmanship
              </li>
              <li>
                <strong>Safety First:</strong> Report any safety concerns
                immediately
              </li>
              <li>
                <strong>Fair Play:</strong> Play at your stated skill level
              </li>
              <li>
                <strong>Communication:</strong> Use the app's messaging features
                appropriately
              </li>
              <li>
                <strong>Reliability:</strong> Show up to games you've committed
                to attend
              </li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>Contact Support</h2>
            <div className="contact-info">
              <p>
                <strong>General Support:</strong>{" "}
                <a href="mailto:support@goalflex.com">support@goalflex.com</a>
              </p>
              <p>
                <strong>Hours:</strong> Monday - Friday, 8:00 AM - 8:00 PM PST
              </p>
              <p>
                <strong>Response Time:</strong> We typically respond within 4-6
                hours during business hours
              </p>
            </div>
          </div>

          <div className="legal-section">
            <h2>Can't Find What You're Looking For?</h2>
            <p>
              If you can't find the answer to your question, don't hesitate to
              reach out to our support team. We're here to help and will get
              back to you as soon as possible.
            </p>
            <div style={{ textAlign: "center", marginTop: "2rem" }}>
              <Link
                to="/contact"
                style={{
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "white",
                  padding: "1rem 2rem",
                  borderRadius: "8px",
                  textDecoration: "none",
                  fontWeight: "600",
                  display: "inline-block",
                }}
              >
                Contact Support Team
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenter;
